<script lang="ts" setup>
import AUApi from "@/api";
import { ref, onMounted, reactive, computed } from "vue";
import { Globals } from "@/Globals";
import { getSiteData, goBack } from "@/NavigationHelper";
import { getKennungValueDisplay, getKennungTitle, getKennungValue } from "@/DataHelper";
import { showSnackbar, scrollIntoView } from "@/UIHelper";
import {} from "@/CalcHelperStatic";
import { globalsBegriffMemo, checkBegriff } from "@/local";
import { mountedBase, BaseSetup, onSave } from "@/DataLoaderStatic";

import {
  createEditorValidator,
  provideEditorValidator,
} from "@/components/EditorValidator";
const validator = createEditorValidator();
provideEditorValidator(validator);
const siteData = getSiteData();

const emit = defineEmits(["back", "save", "update:modelValue"]);
let ort = ref<string>("");
let zielort = ref<string>("");
let selectFahrzeug = ref<boolean>(false);
let fahrtkosten: boolean = false;
let abfahrtKoordinaten = ref<string>("");
let ankunftsKoordinaten = ref<string>("");
let showKoordinaten = ref<boolean>(false);
let loading = ref<boolean>(false);
let addresseAbfahrt = ref<any>();
let addresseAnkunft = ref<any>();
let routeDauer = ref<number>();
let routeKm = ref<number>();
let componentKey = ref<number>(0);

const pageParams: DataPageParameter = {
  backOnSave: true,
  zeitbereich: siteData?.zeitbereich || AUApi.Current.zeitbereich,
  insertMode: false,
  query: "Web_RK_DienstreiseBeginn",
  emitBack: () => emit("back"),
};
const uiData = reactive<DataPageUIData>(BaseSetup(pageParams));

onMounted(async () => {
  uiData.isValueChangeTrackerDisabled = true;
  try {
    await mountedBase(uiData, pageParams, false);
    if (uiData.data.length == 0) throw new Error("no datarow found!");
    if (!uiData.showLoading && uiData.data) {
      const row = uiData.data[0];
      // Koordinaten nur anzeigen wenn Spalten auch da sind!
      const abfahrt = getKennungTitle("RK_ABFAHRTSKOORIDNATEN", uiData.columns);
      if (abfahrt) {
        showKoordinaten.value = true;
      }
      addresseAbfahrt.value = getKennungValue(
        row,
        "RK_ABFAHRTSKOORIDNATEN",
        uiData.columns
      );

      if (!showKoordinaten.value) {
        ort.value = getKennungValueDisplay(row, "RK_ABFAHRTSORT", uiData.columns);
      }
      selectFahrzeug.value = getKennungValueDisplay(
        row,
        "RK_TRANSPORTMITTEL",
        uiData.columns
      );
      const ereignis = getKennungValueDisplay(row, "RK_REISEEREIGNISSE", uiData.columns);
      if (!ereignis) {
        const meldung =
          "Erfassung der Reise nicht möglich, nachfolgende Reisen wurden bereits freigegeben!";
        showSnackbar({
          text: meldung,
          color: "warning",
          duration: 4000,
          closedManual: true,
        });
        emit("back");
      } else if (!ort.value && !showKoordinaten.value) {
        const meldung =
          "Es wurde kein Dienstort erfasst - bitte ergänzen Sie diese Daten!";
        showSnackbar({
          text: meldung,
          color: "warning",
        });
      }
    } else {
      const meldung =
        "Erfassung der Reise nicht möglich, nachfolgende Reisen wurden bereits freigegeben!";
      showSnackbar({
        text: meldung,
        duration: 4000,
        color: "warning",
      });
      emit("back");
    }
    uiData.initialized = true;
  } catch (error) {
    console.error(error);
  }
});
async function onValidate() {
  return await validator.validateAsync();
}
async function save() {
  const isvalid = await onValidate();
  if (!isvalid.valid) {
    showSnackbar({
      text:
        "Validierung fehlgeschlagen - Bitte prüfen! (" +
        isvalid.invalidFields.join(", ") +
        ")",
      duration: 4000,
      color: "error",
    });
    return;
  }
  let varUebergabe: VarUebergabe[] = [];
  const siteData = getSiteData();
  if (siteData.varUbergabe) {
    varUebergabe = siteData.varUbergabe;
  }
  let ortText = "";
  if (
    (ort.value && zielort.value) ||
    (abfahrtKoordinaten.value && ankunftsKoordinaten.value)
  ) {
    const aicAbfAll = 0;

    const res = await onSave(
      uiData,
      pageParams,
      aicAbfAll,
      "",
      undefined,
      0,
      true,
      varUebergabe
    );
    const updateData = res.data;
    siteData.returnedData = updateData;
    pageParams.emitBack(updateData);
  } else {
    if (!zielort.value) {
      ortText = getKennungTitle("CITY", uiData.columns);
    } else {
      ortText = getKennungTitle("RK_ABFAHRTSORT", uiData.columns);
    }
    let meldung = globalsBegriffMemo(
      Globals.Begriff,
      "WEB_SpeichernNichtMoeglich",
      "Speichern kann nicht durchgeführt werden da folgende Daten leer sind"
    );
    meldung = meldung + ": " + ortText;
    showSnackbar({
      text: meldung,
      color: "warning",
    });
  }
}
function checkValue(evt: any) {
  fahrtkosten = evt.value.setShow;
}
function scrollMeIntoView(e: Event) {
  scrollIntoView(e);
}
async function findRoute() {
  loading.value = true;
  if (addresseAbfahrt.value && addresseAnkunft.value) {
    const response = await AUApi.Current.getRouteAPICall(
      addresseAbfahrt.value,
      addresseAnkunft.value
    );
    const info = response.data.info;
    if (response.data.msgType) {
      loading.value = false;
      if (response.data.msgType === "Error") {
        showSnackbar({
          text: info,
          color: "error",
        });
      } else if (response.data.msgType === "Info") {
        showSnackbar({
          text: info,
          color: "info",
        });
      } else if (response.data.msgType === "Warnung") {
        showSnackbar({
          text: info,
          color: "warning",
        });
      }
    } else {
      routeDauer.value = response.data.dauer.toFixed(2);
      routeKm.value = response.data.km.toFixed(2);
      loading.value = false;
    }
  } else {
    alert("bitte alle Adressen angeben!");
    loading.value = false;
  }
}
function setValueAnkunft(res: any) {
  if (!res.kz) {
    addresseAnkunft.value = undefined;
  } else {
    addresseAnkunft.value = res;
  }
  componentKey.value += 1;
}
function setValueAbfahrt(res: any) {
  addresseAbfahrt.value = res;
}
const cardStyle = computed(() => {
  if (window.innerWidth <= 600) {
    return `min-width: ${window.innerWidth}px`;
  }
  return "min-width: 600px";
});
</script>
<template>
  <v-card class="vdialog" :style="cardStyle" :loading="uiData.showLoading">
    <v-card-text v-if="uiData.initialized" pt-2>
      <EditTextField kennung="REISE_BEZEICHNUNG" />
      <EditComboBox kennung="BEW_REISEGRUND" />
      <EditComboBox
        v-if="!showKoordinaten"
        :modelValue="ort"
        @update:modelValue="ort = $event"
        kennung="RK_ABFAHRTSORT"
        :queryZeitbereich="pageParams.zeitbereich"
        :loadFavorite="true"
        :clearable="true"
      />
      <EditGps
        v-if="showKoordinaten"
        :modelValue="abfahrtKoordinaten"
        @update:modelValue="abfahrtKoordinaten = $event"
        kennung="RK_ABFAHRTSKOORIDNATEN"
        :queryZeitbereich="pageParams.zeitbereich"
        :showMap="true"
        @change="setValueAbfahrt"
      />
      <EditComboBox
        v-if="!showKoordinaten"
        kennung="CITY"
        :modelValue="zielort"
        @update:modelValue="zielort = $event"
        :queryZeitbereich="pageParams.zeitbereich"
        :loadFavorite="true"
        :clearable="true"
      />
      <EditGps
        v-if="showKoordinaten"
        :modelValue="ankunftsKoordinaten"
        @update:modelValue="ankunftsKoordinaten = $event"
        kennung="RK_ANKUNFTSKOORIDNATEN"
        :queryZeitbereich="pageParams.zeitbereich"
        :loadFavorite="true"
        @change="setValueAnkunft"
      />
      <v-divider color="red"></v-divider>
      <EditTextArea kennung="BEMERKUNG" />

      <EditToggleSight
        v-if="checkBegriff('Web_RK_Fahrzeugwechsel')"
        kennung="RK_TRANSPORTMITTEL"
        :queryZeitbereich="pageParams.zeitbereich"
        v-model="selectFahrzeug"
        @seteditvalue="checkValue"
      />
    </v-card-text>
    <v-divider></v-divider>
    <v-card-actions :key="componentKey">
      <v-menu
        v-if="showKoordinaten && addresseAnkunft"
        open-on-click
        top
        offset-y
        :close-on-click="true"
        :close-on-content-click="false"
        :key="componentKey"
      >
        <template v-slot:activator="{ props }">
          <v-btn
            color="warning"
            prepend-icon="mdi-highway"
            variant="outlined"
            v-bind="props"
            @click="findRoute"
            text="Route berechnen"
          >
          </v-btn>
          <v-progress-circular
            v-if="loading"
            indeterminate
            color="primary"
            size="20"
            class="mr-2"
          ></v-progress-circular>
        </template>
        <v-card min-width="400px" title="Adresse:">
          <v-card
            variant="outlined"
            density="compact"
            color="surface-variant"
            class="ma-2"
          >
            <v-card-text>
              <div>Dauer: {{ routeDauer }} h</div>
              <div>KM: {{ routeKm }} km</div>
            </v-card-text>
          </v-card>
          <!--     <EditGps :standort="{ lat: gv.lat, lng: gv.lon }"></EditGps> -->
          <v-divider></v-divider>
          <v-card-actions color="white" class="pa-0">
            <v-spacer></v-spacer>
            <!-- <v-btn
            variant="text"
            color="success"
            icon="mdi-check"
            @click="selectionFinished()"
          ></v-btn> -->
          </v-card-actions>
        </v-card>
      </v-menu>
      <v-spacer></v-spacer>
      <CommonBaseButtons
        :deleteVisible="false"
        :createVisible="false"
        :refreshVisible="false"
        :backVisible="false"
        :saveVisible="true"
        @back="goBack()"
        @save="save"
      ></CommonBaseButtons>
    </v-card-actions>
  </v-card>
</template>
<style scoped>
.row {
  padding: 0px;
}
.v-card__subtitle,
.v-card__text,
.v-card__title {
  padding-top: 2px;
}
.vdialog {
  padding: 0px;
}
@media (max-width: 550px) {
  .vdialog {
    padding: 0px;
    width: 100%;
    height: 100%;
    max-height: 100% !important;
    min-height: 100% !important;
  }
}
</style>
